import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../store';
import { setSelectedCategory, setSelectedMenuItem, setCurrentScreen, clearCart, setLoyaltyCode } from '../../store/orderSlice';
import { MenuItem } from '../../types';
import Button from '../ui/Button';
import StartOverButton from '../ui/StartOverButton';
import PanarottisIcon from '../../data/PanaImages/PanarottisIcon.png';
import CartImage from '../../data/PanaImages/CartImage.png';

const MenuScreen: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { selectedCategory, cart, menuCategories, menuItems } = useSelector((state: RootState) => state.order);

  const handleCategorySelect = (categoryId: string) => {
    dispatch(setSelectedCategory(categoryId));
  };

  const handleItemSelect = (item: MenuItem) => {
    dispatch(setSelectedMenuItem(item));
    dispatch(setCurrentScreen('item-detail'));
  };

  const handleViewCart = () => {
    dispatch(setCurrentScreen('cart'));
  };

  const filteredItems = menuItems.filter(item => item.category === selectedCategory);
  const cartTotal = cart.reduce((sum, item) => sum + item.totalPrice, 0);
  const cartItemCount = cart.reduce((sum, item) => sum + item.quantity, 0);
  const FALLBACK_IMG = 'https://images.spurcorp.com/D16EAE6E-A30A-4E5B-A97A-F654B1647789';
  const [randPart, centsPart] = cartTotal.toFixed(2).split('.');

  const selectedCategoryData = menuCategories.find(cat => cat.id === selectedCategory);
  const headingText = (selectedCategoryData?.name || 'Menu').toUpperCase();
  const headingBroken = headingText.includes(' ')
    ? headingText.replace(' ', '\n')
    : headingText;
  const handleStartOver = () => {
    dispatch(clearCart());
    dispatch(setLoyaltyCode(null));
    dispatch(setCurrentScreen('welcome'));
  };

  return (
    <div className="h-screen flex bg-[#f2f1ec]">
      {/* Sidebar */}
      <aside
        className="w-64 bg-transparent shadow-none flex flex-col"
        style={{
          fontFamily:
            '"Nexa Rust Sans Black A", "Museo Sans", ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
        }}
      >
        <div className="px-4 py-10 flex items-center justify-center">
          <img src={PanarottisIcon} alt="Panarottis" className="w-24 h-auto" />
        </div>
        <div className="flex-1 overflow-y-auto py-2 pr-2">
          {menuCategories.map((category, index) => {
            const isSelected = selectedCategory === category.id;
            return (
              <div key={category.id}>
                {index !== 0 && (
                  <div className="mx-auto w-[80%] h-px bg-gray-400" />
                )}
                <button
                  onClick={() => handleCategorySelect(category.id)}
                  className="relative w-full text-left px-6 py-5 transition-all duration-150 active:scale-[0.98]"
                >
                  {isSelected && (
                    <>
                      <span className="absolute left-0 inset-y-0 w-2 bg-red-500" />
                      <span className="absolute inset-y-0 left-2 right-2 bg-[#f2f1ec] rounded-r-2xl rounded-l-none shadow-[0_8px_24px_rgba(0,0,0,0.12)] pointer-events-none" />
                    </>
                  )}
                  <span className={`relative font-display font-black text-[22px] uppercase tracking-[1px] leading-tight block ${
                    isSelected ? 'text-primary' : 'text-[#185C53]'
                  }`}>
                    {category.name}
                  </span>
                </button>
              </div>
            );
          })}
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 flex flex-col h-screen">
        <header className="px-10 pt-8 pb-16">
          <h1 className="font-display font-black text-[#185C53] tracking-[2px] text-[80px] leading-[80px] whitespace-pre-line text-left">
            {headingBroken}
          </h1>
        </header>

        <section className="flex-1 overflow-y-auto px-10 pb-64">
          <div className="grid grid-cols-3 gap-6">
            {filteredItems.map((item) => {
              const [rands, cents] = item.price.toFixed(2).split('.');
              return (
                <div
                  key={item.id}
                  onClick={() => handleItemSelect(item)}
                  className="bg-white rounded-[18px] overflow-hidden cursor-pointer select-none active:scale-[0.98] flex flex-col shadow-md"
                >
                  <div className="h-[180px] w-full bg-gray-100 flex items-center justify-center">
                    <img
                      src={item.image && item.image.length > 0 ? item.image : FALLBACK_IMG}
                      alt={item.displayName}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        (e.currentTarget as HTMLImageElement).src = FALLBACK_IMG;
                      }}
                    />
                  </div>
                  <div className="flex-1 flex flex-col justify-between items-start text-left px-5 py-5 gap-3">
                    <h3 className="font-display font-black text-[18px] tracking-[0.5px] text-kiosk-text leading-snug uppercase text-left">
                      {item.displayName}
                    </h3>
                    <div className="text-primary text-left w-full">
                      <span className="font-display font-black text-[26px] leading-none">{rands}</span>
                      <span className="align-super ml-1 text-[14px]">{cents}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Footer - full width overlay */}
        <div className="fixed left-0 right-0 bottom-0 z-50">
          <div className="bg-white rounded-t-2xl shadow-[0_-10px_30px_rgba(0,0,0,0.15)] px-10 py-6 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="relative">
                <img src={CartImage} alt="Cart" className="w-16 h-16" />
                {cartItemCount > 0 && (
                  <span className="absolute -top-2 -right-2 text-white bg-secondary rounded-full px-2 py-0.5 text-sm font-bold">
                    {cartItemCount}
                  </span>
                )}
              </div>
            <div
              className="flex items-baseline text-kiosk-text"
              style={{
                fontFamily:
                  '"Nexa Rust Sans Black A", "Museo Sans", ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
              }}
            >
              <span className="font-black text-[44px] leading-none">R {randPart}</span>
              <span className="ml-1 text-[24px] leading-none self-start mt-1">{centsPart}</span>
              </div>
            </div>

            <Button
              variant="primary"
              size="xl"
              onClick={handleViewCart}
              disabled={cartItemCount === 0}
              className="px-14 uppercase tracking-[1.2px] text-[24px] font-display rounded-tl-none rounded-tr-[18px] rounded-bl-[18px] rounded-br-none"
            >
              VIEW MY ORDER
            </Button>
          </div>
        <div className="w-full flex justify-center bg-white pt-2 pb-0">
          <StartOverButton onClick={handleStartOver} />
          </div>
        </div>
      </main>
    </div>
  );
};

export default MenuScreen; 